import React from 'react'
import { Navigate, useParams } from 'react-router-dom'
import { Box, Alert } from '@mui/material'
import { useQuery } from '@apollo/client'
import _sortBy from 'lodash/sortBy'
import _get from 'lodash/get'
import _groupBy from 'lodash/groupBy'

import Loading from '../../components/Loading'
import activitiesCountQuery from '../../graphql/query/activitiesCount'
import eventByIdQuery from '../../graphql/query/eventById'
import participantsCountByGenderQuery from '../../graphql/query/participantsCountByGender'
import { StockHeader, StockSummaryCards, StockShirtTable } from '../../components/stocks'

const StockPage = () => {
  const { eventId } = useParams()
  const { data: eventData, loading: eventLoading } = useQuery(eventByIdQuery, {
    variables: { _id: eventId },
  })
  const activitiesCount = useQuery(activitiesCountQuery, {
    variables: { eventId, interval: 20 },
    pollInterval: 20000,
    fetchPolicy: 'network-only',
  })
  // Counted server-side: this page only needs the male/female split, so there is
  // no reason to download every participant on a 20s poll.
  const genderCount = useQuery(participantsCountByGenderQuery, {
    variables: { eventId },
    pollInterval: 20000,
    fetchPolicy: 'network-only',
  })

  if (activitiesCount.loading || eventLoading || genderCount.loading) {
    return <Loading />
  }

  if (genderCount.error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load participant counts: {genderCount.error.message}
      </Alert>
    )
  }

  if (!eventId) {
    return <Navigate to="/events" replace />
  }

  // Calculate shirt distribution progress
  const activitiesCountByShirt = _get(activitiesCount, 'data.activitiesCountByShirt', [])
  let totalRegistered = 0
  let totalReceived = 0

  if (activitiesCountByShirt && activitiesCountByShirt.length > 0) {
    totalRegistered = activitiesCountByShirt.reduce((sum, item) => sum + (item.stock || 0), 0)
    totalReceived = activitiesCountByShirt.reduce((sum, item) => sum + (item.count || 0), 0)
  }

  const totalRemaining = totalRegistered - totalReceived
  const distributionProgress = totalRegistered > 0 ? (totalReceived / totalRegistered) * 100 : 0

  // Calculate gender ratio (the aggregate lower-cases `gender` before grouping)
  const genderCounts = _get(genderCount, 'data.participantsCountByGender', [])
  const countForGender = (gender) =>
    _get(
      genderCounts.find((g) => g.gender === gender),
      'count',
      0
    )
  const maleCount = countForGender('male')
  const femaleCount = countForGender('female')

  // Process shirt data by type
  let shirts = []
  if (activitiesCountByShirt) {
    const shirtTypeGroup = _groupBy(activitiesCountByShirt, 'shirtType')
    const keys = Object.keys(shirtTypeGroup)
    shirts = keys.map((key) => {
      const shirtsx = _sortBy(shirtTypeGroup[key], 'shirtSize')
      let totalRegistered = 0
      let totalReceived = 0
      let totalRemaining = 0

      const tableData = shirtsx.map((s) => {
        const registered = s.stock || 0
        const received = s.count || 0
        const remaining = registered - received
        const progress = registered > 0 ? (received / registered) * 100 : 0

        totalRegistered += registered
        totalReceived += received
        totalRemaining += remaining

        return {
          size: s.shirtSize,
          registered,
          received,
          remaining,
          progress,
        }
      })

      return {
        shirtType: key,
        tableData,
        totalRegistered,
        totalReceived,
        totalRemaining,
        totalProgress: totalRegistered > 0 ? (totalReceived / totalRegistered) * 100 : 0,
      }
    })
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
      {/* Header */}
      <StockHeader />

      {/* Top Information Cards */}
      <StockSummaryCards
        distributionProgress={distributionProgress}
        totalReceived={totalReceived}
        totalRemaining={totalRemaining}
        maleCount={maleCount}
        femaleCount={femaleCount}
      />

      {/* Shirt Type Tables */}
      {shirts &&
        _sortBy(shirts, 'shirtType').map((shirt) => (
          <StockShirtTable key={shirt.shirtType} shirt={shirt} />
        ))}
    </Box>
  )
}

export default StockPage
